import React, { createContext, useContext, useMemo, ReactNode } from "react";
import {
  DashboardApi,
  TeamsApi,
  ChatApi,
  UsersApi,
  VenuesApi,
  StagesApi,
  PerformersApi,
  ShowsApi,
  HoldsApi,
  SubscriptionsApi,
} from "api-client";
import type { CustomUser, Team } from "api-client/models";
import { getApiConfiguration } from "../api.js";

/**
 * TypeScript interfaces for the API context
 */
export type User = CustomUser;
export type { Team };

export interface ApiUrls {
  [key: string]: string;
}

export interface ApiClients {
  dashboard: DashboardApi;
  teams: TeamsApi;
  chat: ChatApi;
  users: UsersApi;
  venues: VenuesApi;
  stages: StagesApi;
  performers: PerformersApi;
  shows: ShowsApi;
  holds: HoldsApi;
  subscriptions: SubscriptionsApi;
}

export interface AppContextType {
  // API clients
  apiClients: ApiClients;
  // Legacy single client for backward compatibility
  apiClient: TeamsApi;

  // App configuration
  isDevMode: boolean;
  serverUrl: string;

  // Django context data
  user: User | null;
  team: Team | null;
  apiUrls: ApiUrls;

  // Helper methods
  getApiClient: (clientType?: keyof ApiClients) => ApiClients[keyof ApiClients];
  getCsrfToken: () => string | null;
}

export interface AppContextProviderProps {
  children: ReactNode;
  user?: User | null;
  team?: Team | null;
  apiUrls?: ApiUrls;
}

// Extend Window interface for Django context
declare global {
  interface Window {
    SERVER_URL_BASE?: string;
    DEBUG?: string | boolean;
    DJANGO_CONTEXT?: {
      debug?: boolean;
      apiBaseUrl?: string;
    };
    urlBase?: string;
  }
}

/**
 * App Context
 */
const AppContext = createContext<AppContextType | null>(null);

/**
 * AppContextProvider - Provides API clients and app-level configuration
 *
 * This context provides:
 * - Configured API client instances (DashboardApi, TeamsApi)
 * - isDevMode boolean flag from Django context
 * - Single initialization per app instance
 */
export const AppContextProvider: React.FC<AppContextProviderProps> = ({
  children,
  user = null,
  team = null,
  apiUrls = {},
}) => {
  // Get Django context values
  const serverUrl = window.SERVER_URL_BASE || window.location.origin;
  const isDevMode =
    window.DEBUG === "True" ||
    window.DEBUG === true ||
    process.env.NODE_ENV === "development";

  // Initialize API clients once using useMemo
  const apiClients = useMemo<ApiClients>(() => {
    const config = getApiConfiguration(serverUrl, team?.id);

    return {
      dashboard: new DashboardApi(config),
      teams: new TeamsApi(config),
      chat: new ChatApi(config),
      users: new UsersApi(config),
      venues: new VenuesApi(config),
      stages: new StagesApi(config),
      performers: new PerformersApi(config),
      shows: new ShowsApi(config),
      holds: new HoldsApi(config),
      subscriptions: new SubscriptionsApi(config),
    };
  }, [serverUrl, team?.id]);

  // Context value object
  const contextValue = useMemo<AppContextType>(
    () => ({
      // API clients
      apiClients,
      // Legacy single client for backward compatibility
      apiClient: apiClients.teams,

      // App configuration
      isDevMode,
      serverUrl,

      // Django context data
      user,
      team,
      apiUrls,

      // Helper methods
      getApiClient: (clientType: keyof ApiClients = "teams") => {
        return apiClients[clientType];
      },
      getCsrfToken: () => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; csrftoken=`);
        if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
        return null;
      },
    }),
    [apiClients, isDevMode, serverUrl, user, team, apiUrls],
  );

  return (
    <AppContext.Provider value={contextValue}>
      <React.Fragment key={team?.id ?? 'no-team'}>{children}</React.Fragment>
    </AppContext.Provider>
  );
};

/**
 * useAppContext - Custom hook to access app context
 *
 * @returns App context with API clients, configuration, and user data
 * @throws Error if used outside of AppContextProvider
 */
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }

  return context;
};

/**
 * Convenience hooks for specific API clients
 */
export const useSubscriptionsApi = (): SubscriptionsApi => {
  const { apiClients } = useAppContext();
  return apiClients.subscriptions;
};

export const useTeamsApi = (): TeamsApi => {
  const { apiClients } = useAppContext();
  return apiClients.teams;
};

export const useDashboardApi = (): DashboardApi => {
  const { apiClients } = useAppContext();
  return apiClients.dashboard;
};

export const useShowsApi = (): ShowsApi => {
  const { apiClients } = useAppContext();
  return apiClients.shows;
};

export const useVenuesApi = (): VenuesApi => {
  const { apiClients } = useAppContext();
  return apiClients.venues;
};

export const useStagesApi = (): StagesApi => {
  const { apiClients } = useAppContext();
  return apiClients.stages;
};

export const usePerformersApi = (): PerformersApi => {
  const { apiClients } = useAppContext();
  return apiClients.performers;
};

export const useHoldsApi = (): HoldsApi => {
  const { apiClients } = useAppContext();
  return apiClients.holds;
};

// ...existing code...

/**
 * Hook for development mode detection
 */
export const useDevMode = (): boolean => {
  const { isDevMode } = useAppContext();
  return isDevMode;
};

/**
 * Hook for accessing current user
 */
export const useUser = (): User | null => {
  const { user } = useAppContext();
  return user;
};

/**
 * Hook for accessing current team
 */
export const useTeam = (): Team | null => {
  const { team } = useAppContext();
  return team;
};

/**
 * Hook for accessing API URLs
 */
export const useApiUrls = (): ApiUrls => {
  const { apiUrls } = useAppContext();
  return apiUrls;
};
